import React, { useEffect, useState } from 'react';
import {
  Drawer, Box, Typography, IconButton,
  Divider, Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import moment from 'moment';
import api from '../../helpers/util';
import cowIcon   from '../../assets/images/cow.png';
import goatIcon  from '../../assets/images/goat.png';
import pigIcon   from '../../assets/images/pig.png';
import sheepIcon from '../../assets/images/sheep.png';

const DRAWER_WIDTH = 400;

const formatDate = (val) => {
  if (!val) return null;
  const d = moment(val);
  return d.isValid() ? d.format('YYYY-MM-DD') : val;
};

const formatDateTime = (val) => {
  if (!val) return null;
  const d = moment(val);
  return d.isValid() ? d.format('YYYY-MM-DD HH:mm') : val;
};

const isEmpty = (val) => !val || val === '0' || val === 0 || val === 'NULL' || val === 'null';

const SPECIES_ICON = {
  BOVINE:  cowIcon,
  OVINE:   sheepIcon,
  PORCINE: pigIcon,
  CAPRINE: goatIcon,
};

const SPECIES_COLOR = {
  BOVINE:  '#8D6E63', 
  OVINE:   '#2E7D32',  
  PORCINE: '#e99adfff',  
  CAPRINE: '#A5D6A7',  
};


function SectionLabel({ children }) {
  return (
    <Typography
      variant="overline"
      color="text.secondary"
      sx={{ fontSize: 11, letterSpacing: 1, fontWeight: 600 }}>
      {children}
    </Typography>
  );
}

function MetaRow({ label, value, mono }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75 }}>
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: 14 }}>
        {label}
      </Typography>
      <Typography variant="body2"
        sx={{ fontSize: 14, fontWeight: 500, fontFamily: mono ? 'monospace' : undefined }}>
        {value ?? '—'}
      </Typography>
    </Box>
  );
}

function TimelineStep({ done, failed, label, timestamp, message, isLast }) {
  const icon = done
    ? <CheckCircleIcon sx={{ fontSize: 22, color: 'success.main' }} />
    : failed
    ? <ErrorIcon sx={{ fontSize: 22, color: 'error.main' }} />
    : <RadioButtonUncheckedIcon sx={{ fontSize: 22, color: 'text.disabled' }} />;

  return (
    <Box sx={{ display: 'flex', gap: 2 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {icon}
        {!isLast && (
          <Box sx={{ width: '2px', flex: 1, minHeight: 24, mt: 0.5,
            bgcolor: done ? 'success.light' : 'divider' }} />
        )}
      </Box>
      <Box sx={{ pb: isLast ? 0 : 2.5 }}>
        <Typography variant="body2" sx={{ fontSize: 14, fontWeight: 400, lineHeight: 1.4 }}>
          {label}
        </Typography>
        {timestamp && (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13, mt: 0.25 }}>
            {timestamp}
          </Typography>
        )}
        {message && (
          <Typography variant="body2"
            sx={{ fontSize: 12, mt: 0.5, px: 1.25, py: 0.5,
              bgcolor: failed ? 'error.lighter' : 'action.hover',
              color: failed ? 'error.dark' : 'text.secondary',
              borderRadius: 1, fontStyle: 'italic' }}>
            {message}
          </Typography>
        )}
      </Box>
    </Box>
  );
}


export default function ProcessMonitorDrawer({ open, onClose, body, companyId }) {
  const [emailLog, setEmailLog]         = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);

  useEffect(() => {
    if (!open || !body || !companyId) return;

    setEmailLog(null);
    setEmailLoading(true);

    const productionDate = formatDate(body.production_date);

    api.get('/email-log/body', {
      params: {
        company_id:      companyId,
        owner_name:      body.owner  || '',
        origin_name:     body.origin || '',
        production_date: productionDate,
      },
    })
      .then((res) => setEmailLog(res.data.result))
      .catch((err) => console.error('Email log fetch error:', err))
      .finally(() => setEmailLoading(false));
  }, [open, body?.body_number, body?.production_date]);

  if (!body) return null;

  const nlisUploaded  = !!body.upload_time;
  const reportSent    = !!emailLog && emailLog.status !== 'bounced';
  const reportBounced = !!emailLog && emailLog.status === 'bounced';

  const fatLabel = body.species === 'BOVINE' ? 'Fat depth' : 'Fat score';
  const fatValue = body.species === 'BOVINE' ? body.fat_depth : body.fat_score;

  const hasRfid   = !isEmpty(body.rfid_no);
  const hasNlisId = !isEmpty(body.nlis_id);
  const hasPic    = !isEmpty(body.pic);

  const speciesKey   = (body.species || '').toUpperCase();
  const speciesIcon  = SPECIES_ICON[speciesKey] || null;
  const speciesColor = SPECIES_COLOR[speciesKey] || null;

  const handleCopyRfid = () => {
    if (hasRfid) navigator.clipboard.writeText(body.rfid_no);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: DRAWER_WIDTH, maxWidth: '100vw', display: 'flex', flexDirection: 'column' },
      }}
    >
      {/* ── Header ── */}
      <Box sx={{
        px: 2.5, py: 2.5,
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        borderBottom: '1px solid', borderColor: 'divider',
        backgroundColor: speciesColor || 'background.paper',
        transition: 'background-color 0.2s',
      }}>
        <Box>
          <Typography sx={{
            fontSize: 22, fontWeight: 700, lineHeight: 1.3,
            color: speciesColor ? '#fff' : 'text.primary',
          }}>
            Body {body.body_number}
          </Typography>
          <Typography variant="body2" sx={{
            fontSize: 15, mt: 0.5,
            color: speciesColor ? 'rgba(255,255,255,0.85)' : 'text.secondary',
          }}>
            {formatDate(body.production_date)} · {body.species}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {speciesIcon && (
            <img
              src={speciesIcon}
              alt={body.species}
              style={{
                width: 48,
                height: 48,
                opacity: 0.9,
                filter: speciesColor ? 'brightness(0) invert(1)' : 'none',
              }}
            />
          )}
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="Close drawer"
            sx={{ color: speciesColor ? '#fff' : 'inherit' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2 }}>

        <SectionLabel>Body details</SectionLabel>
        <Box sx={{ mt: 0.5, mb: 2 }}>
          <MetaRow label="Lot no"    value={body.lot_no} />
          <MetaRow label="Lot size"  value={body.lot_size} />
          <MetaRow label="Owner"     value={body.owner} />
          <MetaRow label="Origin"    value={body.origin} />
          <MetaRow label="HSCW"      value={body.hscw ? `${body.hscw} kg` : null} />
          <MetaRow label="Sex"       value={body.sex} />
          <MetaRow label={fatLabel}  value={fatValue} />
          <MetaRow label="Category"  value={body.category} />
          <MetaRow label="MSA"       value={body.msa} />
          <MetaRow label="HGP"       value={body.hgp} />
        </Box>

        {(hasRfid || hasNlisId || hasPic) && (
          <>
            <Divider sx={{ mb: 1.5 }} />
            <SectionLabel>NLIS</SectionLabel>
            <Box sx={{ mt: 0.5, mb: 2 }}>
              {hasPic && <MetaRow label="PIC" value={body.pic} mono />}
              {hasRfid && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: 14 }}>
                    RFID
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2"
                      sx={{ fontSize: 14, fontWeight: 500, fontFamily: 'monospace' }}>
                      {body.rfid_no}
                    </Typography>
                    <Tooltip title="Copy RFID">
                      <IconButton size="small" onClick={handleCopyRfid} sx={{ p: 0.25 }}>
                        <ContentCopyIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              )}
              {hasNlisId && <MetaRow label="NLIS ID" value={body.nlis_id} mono />}
            </Box>
          </>
        )}

        <Divider sx={{ mb: 1.5 }} />
        <SectionLabel>Process timeline</SectionLabel>
        <Box sx={{ mt: 1.5 }}>
          <TimelineStep
            done
            label="Kill recorded"
            timestamp={formatDateTime(body.process_time)}
          />
          <TimelineStep
            done={nlisUploaded}
            label="NLIS uploaded"
            timestamp={formatDateTime(body.upload_time)}
            message={body.nlis_response || null}
          />
          <TimelineStep
            done={reportSent}
            failed={reportBounced}
            label="Report sent"
            timestamp={emailLoading ? null : formatDateTime(emailLog?.sent_at)}
            message={
              emailLoading
                ? null
                : reportBounced
                ? `Bounced: ${emailLog.bounce_reason || 'unknown reason'}`
                : emailLog
                ? `To: ${emailLog.recipient}`
                : null
            }
            isLast
          />
        </Box>

      </Box>
    </Drawer>
  );
}